import { createListCollection } from '@ark-ui/react/collection';
import { ArrowDown, Checkmark } from '@ui/components/icons';
import { Icon } from '@ui/lib/park/ui/icon';
import { Select } from '@ui/lib/park/ui/select';
import { Tabs } from '@ui/lib/park/ui/tabs';
import { useTranslation } from 'next-i18next';
import { Box, Flex, HStack, styled } from 'styled-system/jsx';

import { CompetitionFilterMode, CompetitionStatus, CompetitionType, SortOption } from './types';

/** V2 count chip — mono numeral on surface2; long-dim/long when the tab is selected */
const CountBadge = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '18px',
    height: '16px',
    px: '5px',
    ml: '6px',
    borderRadius: '2px',
    fontFamily: 'Inter',
    fontSize: '10px',
    fontWeight: '500',
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    background: 'v2.bg.surface2',
    color: 'v2.text.subtle',
    transition: 'color 120ms ease, background 120ms ease',
  },
});

const selectedCountCss = {
  '&[data-selected] [data-role="count"]': {
    background: 'v2.long.dim',
    color: 'v2.long.default',
  },
} as const;

interface CompetitionFiltersProps {
  currentFilterMode: CompetitionFilterMode;
  onFilterModeChange: (mode: CompetitionFilterMode) => void;
  currentType: CompetitionType | 'All';
  onTypeChange: (type: CompetitionType | 'All') => void;
  currentSort: SortOption;
  onSortChange: (sort: SortOption) => void;
  statusCounts?: Partial<Record<CompetitionStatus, number>>;
  joinedCount?: number;
  isConnected?: boolean;
}

export const CompetitionFilters = ({
  currentFilterMode,
  onFilterModeChange,
  currentType,
  onTypeChange,
  currentSort,
  onSortChange,
  statusCounts,
  joinedCount,
  isConnected,
}: CompetitionFiltersProps) => {
  const { t } = useTranslation();

  const statusTabs = [
    CompetitionFilterMode.ONGOING,
    CompetitionFilterMode.UPCOMING,
    CompetitionFilterMode.ENDED,
  ];

  const typeCollection = createListCollection({
    items: [
      { label: 'All competitions', value: 'All' },
      { label: CompetitionType.VOLUME, value: CompetitionType.VOLUME },
      { label: CompetitionType.PNL, value: CompetitionType.PNL },
    ],
  });

  const sortCollection = createListCollection({
    items: [
      { label: 'New to old', value: SortOption.NEW_TO_OLD },
      { label: 'Old to new', value: SortOption.OLD_TO_NEW },
    ],
  });

  const ghostTriggerProps = {
    h: '28px',
    minH: '28px',
    px: '10px',
    borderRadius: '2px',
    border: '1px solid',
    borderColor: 'v2.border.default',
    bg: 'transparent',
    color: 'v2.text.muted',
    textStyle: 'v2.label',
    transition: 'border-color 120ms ease, color 120ms ease',
    _hover: { borderColor: 'v2.border.strong', color: 'v2.text.primary' },
  } as const;

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      justify="space-between"
      align={{ base: 'stretch', md: 'center' }}
      gap={2}
      borderBottom="1px solid"
      borderBottomColor="v2.border.default"
    >
      <Tabs.Root
        value={currentFilterMode}
        onValueChange={(e) => onFilterModeChange(e.value as CompetitionFilterMode)}
        variant="textLine"
        size="md"
      >
        <Tabs.List
          gap="16px"
          overflowX="auto"
          css={{
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {statusTabs.map((mode) => (
            <Tabs.Trigger
              key={mode}
              value={mode}
              h="38px"
              px="12px"
              textStyle="v2.ui"
              css={selectedCountCss}
            >
              {mode}
              {typeof statusCounts?.[mode as unknown as CompetitionStatus] === 'number' &&
                statusCounts[mode as unknown as CompetitionStatus]! > 0 && (
                  <CountBadge data-role="count">
                    {statusCounts[mode as unknown as CompetitionStatus]}
                  </CountBadge>
                )}
            </Tabs.Trigger>
          ))}
          {/* Divider before Joined tab */}
          {isConnected && (
            <>
              <Box
                w="1px"
                h="16px"
                bg="v2.border.default"
                mx={2}
                alignSelf="center"
                flexShrink={0}
              />
              <Tabs.Trigger
                value={CompetitionFilterMode.JOINED}
                h="38px"
                px="12px"
                textStyle="v2.ui"
                css={selectedCountCss}
              >
                {t('competition.joined', 'Joined')}
                {typeof joinedCount === 'number' && joinedCount > 0 && (
                  <CountBadge data-role="count">{joinedCount}</CountBadge>
                )}
              </Tabs.Trigger>
            </>
          )}
          <Tabs.Indicator />
        </Tabs.List>
      </Tabs.Root>

      <HStack gap="8px" pb={{ base: '8px', md: '0' }} flexShrink={0}>
        <Box>
          <Select.Root
            collection={typeCollection}
            value={[currentType]}
            onValueChange={(e) =>
              onTypeChange(e.value[0] as CompetitionType | 'All')
            }
            positioning={{ sameWidth: true }}
            size="md"
          >
            <Select.Control>
              <Select.Trigger minW="140px" {...ghostTriggerProps}>
                <Select.ValueText placeholder="Select type" />
                <Icon width="12px" height="12px">
                  <ArrowDown />
                </Icon>
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {typeCollection.items.map((item) => (
                  <Select.Item textStyle="v2.label" key={item.value} item={item}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator>
                      <Checkmark />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>

        <Box>
          <Select.Root
            collection={sortCollection}
            value={[currentSort]}
            onValueChange={(e) => onSortChange(e.value[0] as SortOption)}
            positioning={{ sameWidth: true }}
          >
            <Select.Control>
              <Select.Trigger minW="110px" {...ghostTriggerProps}>
                <Select.ValueText placeholder="Sort by" />
                <Icon width="12px" height="12px">
                  <ArrowDown />
                </Icon>
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {sortCollection.items.map((item) => (
                  <Select.Item textStyle="v2.label" key={item.value} item={item}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator>
                      <Checkmark />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
      </HStack>
    </Flex>
  );
};
